import { useState } from 'react';
import { FilterBar } from '../components/Filters';
import { StackedBarChart, ChartLegend, type Series } from '../components/charts';
import {
  CAT_COLORS,
  downloadCsv,
  localTime,
  num,
  pct,
  useApi,
  useFilters,
  useQuery,
  type DifotResponse,
  type FiltersResponse,
  type StopsResponse,
  type StopRow,
} from '../lib/reports';

const TREND_SERIES: Series[] = [
  { key: 'on_time', label: 'On time', color: CAT_COLORS.on_time },
  { key: 'early', label: 'Early', color: CAT_COLORS.early },
  { key: 'late', label: 'Late', color: CAT_COLORS.late },
  { key: 'no_eta', label: 'No ETA', color: CAT_COLORS.no_eta },
  { key: 'failed', label: 'Failed', color: CAT_COLORS.failed },
  { key: 'cancelled', label: 'Cancelled', color: CAT_COLORS.cancelled },
];

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function formatDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return `${WEEKDAY[d.getUTCDay()]} ${d.getUTCDate()}/${d.getUTCMonth() + 1}`;
}

export function Difot() {
  const [filters, patch] = useFilters();
  const qs = useQuery(filters);
  const [category, setCategory] = useState<string>('');

  const { data, error, loading } = useApi<DifotResponse>(`/api/reports/difot?${qs}`);
  const { data: options } = useApi<FiltersResponse>('/api/reports/difot/filters');
  const stopsQs = `${qs}${category ? `&category=${encodeURIComponent(category)}` : ''}&limit=200`;
  const { data: stops } = useApi<StopsResponse>(`/api/reports/difot/stops?${stopsQs}`);

  const s = data?.summary;
  const aw = data?.arrival_window;

  function exportStops() {
    const rows = (stops?.rows ?? []).map((r: StopRow) => [
      r.stop_id,
      r.trip_date,
      r.status,
      r.arrival_cat,
      r.driver_name,
      r.customer_name,
      r.region,
      r.address,
      r.win_start,
      r.win_end,
      r.arrived_time,
      r.completed_time,
    ]);
    downloadCsv(
      `amart-difot-${filters.from}_${filters.to}.csv`,
      ['stop_id', 'trip_date', 'status', 'category', 'driver', 'store', 'region', 'address', 'window_start', 'window_end', 'arrived', 'completed'],
      rows,
    );
  }

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <p className="eyebrow">Reporting / DIFOT</p>
          <h1>DIFOT</h1>
          <p className="muted">
            {s ? `${num(s.stops)} stops · ` : ''}
            {filters.from} → {filters.to} · times shown in local timezone of the stop
          </p>
        </div>
      </header>

      <FilterBar filters={filters} options={options} onChange={patch} onExport={exportStops} />

      {error && <p className="form-error" role="alert">{error}</p>}
      {loading && !data && <p className="muted">Loading…</p>}

      {s && aw && (
        <>
          <div className="hero">
            <div className="hero-main">
              <span className="hero-label">DIFOT</span>
              <span className="hero-value">{pct(s.difot_pct)}</span>
              <span className="hero-sub">on-time arrivals ÷ all stops in window</span>
            </div>
            <div className="hero-stats">
              <div>
                <span className="hs-label">vs previous period</span>
                <span className={`hs-value ${(s.difot_delta_pp ?? 0) >= 0 ? 'up' : 'down'}`}>
                  {(s.difot_delta_pp ?? 0) >= 0 ? '+' : ''}
                  {s.difot_delta_pp ?? 0} pp
                </span>
                <span className="hs-foot">prev {pct(s.prev_difot_pct)}</span>
              </div>
              <div>
                <span className="hs-label">On time or early</span>
                <span className="hs-value">{pct(s.on_time_or_early_pct)}</span>
                <span className="hs-foot">{num(s.on_time + s.early)} stops</span>
              </div>
              <div>
                <span className="hs-label">Late</span>
                <span className="hs-value">{pct(s.late_pct)}</span>
                <span className="hs-foot">{num(s.late)} stops</span>
              </div>
              <div>
                <span className="hs-label">Failed + cancelled</span>
                <span className="hs-value">{pct(s.failed_cancelled_pct)}</span>
                <span className="hs-foot">{num(s.failed + s.cancelled)} stops</span>
              </div>
            </div>
          </div>

          <div className="tiles">
            <Tile label="Stops" value={num(s.stops)} color={CAT_COLORS.early} active={category === ''} onClick={() => setCategory('')} />
            <Tile label="Completed" value={num(s.completed)} sub={pct(s.completed_pct, 2)} color={CAT_COLORS.on_time} active={category === 'Complete'} onClick={() => setCategory('Complete')} />
            <Tile label="Failed" value={num(s.failed)} sub={pct(s.failed_pct, 2)} color={CAT_COLORS.late} active={category === 'Failed'} onClick={() => setCategory('Failed')} />
            <Tile label="Cancelled" value={num(s.cancelled)} sub={pct(s.cancelled_pct, 2)} color={CAT_COLORS.no_eta} active={category === 'Cancelled'} onClick={() => setCategory('Cancelled')} />
          </div>

          <div className="grid-2">
            <div className="card">
              <div className="card-head">
                <div>
                  <p className="eyebrow">DIFOT trend</p>
                  <h2>Stops per day</h2>
                  <p className="muted small">Stacked by DIFOT category · click a bar to filter the table</p>
                </div>
                <ChartLegend series={TREND_SERIES} />
              </div>
              <StackedBarChart
                data={data.trend}
                series={TREND_SERIES}
                xKey="day"
                formatX={formatDay}
                onBarClick={() => setCategory('')}
              />
            </div>

            <div className="card">
              <p className="eyebrow">Arrival time window</p>
              <h2>Categorisation</h2>
              <div className="tiles tiles-col">
                <Tile label="Early" value={num(aw.early)} sub={pct(aw.early_pct, 2)} color={CAT_COLORS.early} active={category === 'Early'} onClick={() => setCategory('Early')} />
                <Tile label="On time" value={num(aw.on_time)} sub={pct(aw.on_time_pct, 2)} color={CAT_COLORS.on_time} active={category === 'On time'} onClick={() => setCategory('On time')} />
                <Tile label="Late" value={num(aw.late)} sub={pct(aw.late_pct, 2)} color={CAT_COLORS.late} active={category === 'Late'} onClick={() => setCategory('Late')} />
                <Tile label="No ETA" value={num(aw.no_eta)} sub={pct(aw.no_eta_pct, 2)} color={CAT_COLORS.no_eta} active={category === 'No ETA'} onClick={() => setCategory('No ETA')} />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <div>
                <h2>Stops {category && <span className="chip">{category}</span>}</h2>
                <p className="muted small">
                  Showing {num(stops?.rows.length ?? 0)} of {num(stops?.total ?? 0)} matching stops
                  {category && (
                    <button type="button" className="link-btn" onClick={() => setCategory('')}>
                      clear filter
                    </button>
                  )}
                </p>
              </div>
            </div>
            <StopsTable rows={stops?.rows ?? []} />
          </div>

          <ol className="defs">
            <li><b>Early</b> · Arrival is before the day-before SMS time window.</li>
            <li><b>On time</b> · Arrival (or completion) falls inside the SMS time window.</li>
            <li><b>Late</b> · Arrival is after the SMS time window.</li>
            <li><b>No ETA</b> · No day-before time-window SMS was sent — early/on time/late is N/A.</li>
          </ol>
        </>
      )}
    </section>
  );
}

function Tile({
  label,
  value,
  sub,
  color,
  active,
  onClick,
}: {
  label: string;
  value: string;
  sub?: string;
  color: string;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button type="button" className={`tile${active ? ' active' : ''}`} style={{ borderTopColor: color }} onClick={onClick}>
      <span className="tile-label">{label}</span>
      <span className="tile-value">{value}</span>
      {sub && <span className="tile-sub">{sub}</span>}
    </button>
  );
}

function StopsTable({ rows }: { rows: StopRow[] }) {
  if (rows.length === 0) return <p className="muted">No stops match the current filters.</p>;
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>Trip date</th>
            <th>Status</th>
            <th>Category</th>
            <th>Driver</th>
            <th>Store</th>
            <th>Region</th>
            <th>SMS window</th>
            <th>Arrived / completed</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.stop_id}>
              <td>{r.trip_date ?? '—'}</td>
              <td>{r.status ?? '—'}</td>
              <td>
                <span className={`cat cat-${(r.arrival_cat ?? '').toLowerCase().replace(/\s+/g, '-')}`}>
                  {r.arrival_cat ?? '—'}
                </span>
              </td>
              <td>{r.driver_name ?? '—'}</td>
              <td>{r.customer_name ?? '—'}</td>
              <td>{r.region ?? '—'}</td>
              <td>
                {r.win_start ? `${localTime(r.win_start, r.tz)} – ${localTime(r.win_end, r.tz)}` : '—'}
              </td>
              <td>{localTime(r.arrived_time ?? r.completed_time, r.tz)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
